import { useTranslation } from "react-i18next";
import { Sparkles, AlertTriangle, FileSearch, ChevronRight } from "lucide-react";
import { REVIEW_CRITERIA as CRIT, isChoice } from "@/data/formConfig";
import { choiceLabel, tallyLabel, fmtDate } from "@/i18n";

// The second reader's (AI) review of one application: scores next to the evaluators' (or, for an
// evaluator, next to their own review), integrity findings first, then the reasons on demand.
// `compare` = {label, scores, overall}; choice scores are a tally (admin) or one answer (evaluator).

const GAP_BIG = 3;
const GAP_NOTE = 1.5;

function Gap({ value }) {
  if (value == null || Number.isNaN(value)) return <span className="text-muted-foreground">—</span>;
  const abs = Math.abs(value);
  const cls = abs >= GAP_BIG ? "text-destructive font-semibold" : abs >= GAP_NOTE ? "text-accent font-medium" : "text-muted-foreground";
  return <span className={cls}>{value > 0 ? "+" : value < 0 ? "−" : ""}{abs.toFixed(1)}</span>;
}

function Details({ title, count, children }) {
  return (
    <details className="group border-t border-border/70 first:border-t-0">
      <summary className="flex items-center gap-2 py-2.5 cursor-pointer list-none text-sm font-medium hover:text-primary">
        <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
        {title}{count != null && <span className="text-xs text-muted-foreground font-normal">({count})</span>}
      </summary>
      <div className="pl-6 pb-3 text-sm leading-relaxed">{children}</div>
    </details>
  );
}

const Bullets = ({ items }) => (items?.length
  ? <ul className="list-disc pl-4 space-y-1 marker:text-muted-foreground">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
  : <p className="text-muted-foreground">—</p>);

export function IntegrityFinding({ f }) {
  const { t } = useTranslation();
  const doc = f.evidence === "document";
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3" data-testid="ai-integrity">
      <span className={`inline-block text-[11px] font-medium rounded-full px-2 py-0.5 mb-1.5 ${doc ? "bg-amber-200/80 text-amber-900" : "bg-muted text-muted-foreground border border-border"}`}>
        {t(doc ? "admin.ai.evidence_document" : "admin.ai.evidence_metadata")}
      </span>
      <p className="text-sm leading-relaxed">{f.finding}</p>
      {f.how_to_settle && (
        <p className="text-xs text-muted-foreground mt-1.5"><span className="font-medium">{t("admin.ai.settle")}</span> {f.how_to_settle}</p>
      )}
    </div>
  );
}

export default function AiReviewCard({ review, compare }) {
  const { t } = useTranslation();
  if (!review) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 mb-6" data-testid="ai-review-empty">
        <h3 className="font-heading text-lg font-medium mb-1 flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> {t("admin.ai.card_title")}</h3>
        <p className="text-sm text-muted-foreground">{t("admin.ai.none")}</p>
      </div>
    );
  }
  const r = review.result || {};
  const integrity = r.integrity || [];
  const fin = r.financial_facts || {};
  const deductions = [...(r.documentation?.deductions || []),
    ...(r.documentation?.self_written_letters ? [{ points: -1.5, reason: r.documentation.self_written_reason || t("admin.ai.self_written") }] : [])];
  const questions = r.interview?.questions || [];
  const conf = (c) => (c ? t("admin.ai.confidence", { level: t(`admin.ai.conf_${c}`) }) : null);
  const testData = review.model === "manual-review";

  const cmpValue = (k) => {
    const v = compare?.scores?.[k];
    if (v == null) return null;
    return isChoice(k) ? (typeof v === "object" ? tallyLabel(v) : choiceLabel(v)) : v;
  };

  return (
    <div id="ai-review" className="bg-card border border-border rounded-xl p-6 mb-6 scroll-mt-6" data-testid="ai-review">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <h3 className="font-heading text-lg font-medium flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> {t("admin.ai.card_title")}</h3>
        <p className="text-xs text-muted-foreground mt-1.5">{review.completed_at ? fmtDate(review.completed_at) : ""} · {review.model}</p>
      </div>
      <p className="text-xs text-muted-foreground mb-4">{t("admin.ai.card_hint")}</p>
      {testData && (
        <p className="text-xs bg-secondary/70 border border-border rounded-md px-3 py-2 mb-4 flex items-center gap-2">
          <FileSearch className="w-3.5 h-3.5 text-primary shrink-0" /> {t("admin.ai.test_data")}
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground text-xs uppercase tracking-wider text-left border-b border-border">
              <th className="py-2 pr-4 font-medium">{t("admin.ai.col_criterion")}</th>
              <th className="py-2 px-2 font-medium text-center">{t("admin.ai.col_ai")}</th>
              {compare && <th className="py-2 px-2 font-medium text-center">{compare.label}</th>}
              {compare && <th className="py-2 pl-2 font-medium text-center">{t("admin.ai.col_gap")}</th>}
            </tr>
          </thead>
          <tbody>
            {CRIT.map((k) => {
              const ai = review.scores[k];
              const other = compare?.scores?.[k];
              const gap = !isChoice(k) && typeof other === "number" ? ai - other : null;
              return (
                <tr key={k} className="border-b border-border/60">
                  <td className="py-2.5 pr-4">{t(`criteria_short.${k}`)}</td>
                  <td className="py-2.5 px-2 text-center font-medium">{isChoice(k) ? choiceLabel(ai) : ai}</td>
                  {compare && <td className="py-2.5 px-2 text-center text-muted-foreground">{cmpValue(k) ?? "—"}</td>}
                  {compare && <td className="py-2.5 pl-2 text-center">{isChoice(k) ? "" : <Gap value={gap} />}</td>}
                </tr>
              );
            })}
            <tr className="bg-secondary/50">
              <td className="py-2.5 pr-4 font-medium">{t("admin.ai.overall")}</td>
              <td className="py-2.5 px-2 text-center font-semibold text-primary">{review.overall?.toFixed(1)}</td>
              {compare && <td className="py-2.5 px-2 text-center font-medium">{compare.overall?.toFixed?.(1) ?? "—"}</td>}
              {compare && <td className="py-2.5 pl-2 text-center"><Gap value={compare.overall != null ? review.overall - compare.overall : null} /></td>}
            </tr>
          </tbody>
        </table>
      </div>

      {integrity.length > 0 && (
        <div className="mt-5">
          <h4 className="text-sm font-medium flex items-center gap-2 mb-2 text-amber-800">
            <AlertTriangle className="w-4 h-4" /> {t("admin.ai.integrity_title", { count: integrity.length })}
          </h4>
          <div className="space-y-2">{integrity.map((f, i) => <IntegrityFinding key={i} f={f} />)}</div>
        </div>
      )}

      {r.summary && (
        <p className="text-sm leading-relaxed mt-5"><span className="font-medium">{t("admin.ai.summary")}:</span> {r.summary}</p>
      )}

      <div className="mt-4 border border-border rounded-lg px-3">
        <Details title={t("admin.ai.reasons_success")}>
          {r.success?.grade != null && (
            <p className="mb-2">
              <span className="font-medium">{t(r.success.grade_scale === "high_school" ? "admin.ai.grade_hs" : "admin.ai.grade_uni", { value: r.success.grade })}</span>
              {r.success.grade_documented === false && <span className="text-muted-foreground"> · {t("admin.ai.grade_claimed")}</span>}
              {r.success.strong_extracurricular && <span className="text-muted-foreground"> · {t("admin.ai.strong_extra")}</span>}
              {r.success.delay ? <span className="text-muted-foreground"> · {t("admin.ai.delay", { points: Math.abs(r.success.delay) })}</span> : null}
            </p>
          )}
          <Bullets items={r.success?.reasons} />
          {conf(r.success?.confidence) && <p className="text-xs text-muted-foreground mt-2">{conf(r.success.confidence)}</p>}
        </Details>
        <Details title={t("admin.ai.reasons_financial")}>
          <p className="mb-2">
            {review.income_per_person != null && <span className="font-medium">{t("admin.ai.income_pp", { value: Math.round(review.income_per_person) })}</span>}
            {fin.household_size ? <span className="text-muted-foreground"> · {t("admin.ai.household", { count: fin.household_size })}</span> : null}
          </p>
          <Bullets items={[...(fin.documented_sources || []), ...(fin.adjustments || []).map((a) => `${a.points > 0 ? "+" : "−"}${Math.abs(a.points)} ${a.reason}`)]} />
          {fin.notes && <p className="mt-2 text-muted-foreground">{fin.notes}</p>}
          {conf(fin.confidence) && <p className="text-xs text-muted-foreground mt-2">{conf(fin.confidence)}</p>}
        </Details>
        <Details title={t("admin.ai.deductions")} count={deductions.filter((d) => d.points).length}>
          {deductions.length ? (
            <ul className="space-y-1">
              {deductions.map((d, i) => (
                <li key={i} className="flex gap-2">
                  <span className={`w-8 shrink-0 text-right tabular-nums ${d.points ? "text-destructive font-medium" : "text-muted-foreground"}`}>{d.points ? `−${Math.abs(d.points)}` : "·"}</span>
                  <span>{d.reason.replace(/^([−-]\d+(?:\.\d+)?\s*)+/, "")}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-muted-foreground">—</p>}
        </Details>
        <Details title={t("admin.ai.questions")} count={questions.length}>
          <Bullets items={questions} />
        </Details>
      </div>
    </div>
  );
}

// Compact version for the side column of the application page: the AI average next to the
// evaluators', and the number of integrity findings, with a link to the full card.
export function AiSideSummary({ review, evaluators }) {
  const { t } = useTranslation();
  if (!review) return null;
  const n = (review.result?.integrity || []).length;
  const gap = evaluators ? review.overall - evaluators.overall : null;
  return (
    <a href="#ai-review" className="block bg-card border border-border rounded-xl p-5 hover:border-primary transition-colors" data-testid="ai-side-summary">
      <p className="text-sm font-medium flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-primary" /> {t("admin.ai.card_title")}</p>
      <div className="flex items-end gap-5">
        <div><p className="font-heading text-2xl font-semibold text-primary leading-none">{review.overall.toFixed(1)}</p><p className="text-xs text-muted-foreground mt-1">{t("admin.ai.col_ai")}</p></div>
        {evaluators && <div><p className="font-heading text-2xl font-semibold leading-none">{evaluators.overall.toFixed(1)}</p><p className="text-xs text-muted-foreground mt-1">{t("admin.ai.col_evaluators")}</p></div>}
        {gap != null && <div><p className="font-heading text-2xl leading-none"><Gap value={gap} /></p><p className="text-xs text-muted-foreground mt-1">{t("admin.ai.col_gap")}</p></div>}
      </div>
      {n > 0 && <p className="text-xs text-amber-800 mt-3 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> {t("admin.ai.integrity_title", { count: n })}</p>}
    </a>
  );
}
